import { useEffect, useState } from 'react';
import { Building2, FileText, Users, Calendar, Briefcase } from 'lucide-react';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

export function CompanyBusinessDetailsStep({ data, onChange }: Props) {
  const [errors, setErrors] = useState({
    businessName: '',
    yearsInBusiness: '',
    bio: ''
  });
  const [touched, setTouched] = useState({
    businessName: false,
    yearsInBusiness: false,
    bio: false
  });

  const validateField = (field: string, value: any) => {
    let error = '';
    
    switch (field) {
      case 'businessName':
        if (!value?.trim()) error = 'Business name is required';
        break;
      case 'yearsInBusiness':
        if (!value || value < 0) error = 'Years in business is required';
        break;
      case 'bio':
        if (!value?.trim()) error = 'Business description is required';
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
    if (touched[field as keyof typeof touched]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Details & Legal</h2>
        <p className="text-gray-600">Provide your company information and legal details</p>
      </div>

      {/* Basic Business Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-2" />
            Business Name *
          </label>
          <input
            type="text"
            value={data.businessName || ''}
            onChange={(e) => updateField('businessName', e.target.value)}
            onBlur={() => handleBlur('businessName')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.businessName && touched.businessName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.businessName && touched.businessName && (
            <p className="text-xs text-red-600 mt-1">{errors.businessName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-2" />
            Years in Business *
          </label>
          <input
            type="number"
            min="0"
            value={data.yearsInBusiness || ''}
            onChange={(e) => updateField('yearsInBusiness', parseInt(e.target.value) || 0)}
            onBlur={() => handleBlur('yearsInBusiness')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.yearsInBusiness && touched.yearsInBusiness ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.yearsInBusiness && touched.yearsInBusiness && (
            <p className="text-xs text-red-600 mt-1">{errors.yearsInBusiness}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Registration Number
          </label>
          <input
            type="text"
            value={data.registrationNumber || ''}
            onChange={(e) => updateField('registrationNumber', e.target.value)}
            placeholder="Company registration number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            VAT Number
          </label>
          <input
            type="text"
            value={data.vatNumber || ''}
            onChange={(e) => updateField('vatNumber', e.target.value)}
            placeholder="VAT registration number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Founded Year
          </label>
          <input
            type="number"
            min="1800"
            max={new Date().getFullYear()}
            value={data.foundedYear || ''}
            onChange={(e) => updateField('foundedYear', parseInt(e.target.value) || '')}
            placeholder="e.g., 2010"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Size
          </label>
          <select
            value={data.companySize || ''}
            onChange={(e) => updateField('companySize', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select company size</option>
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tax Number
          </label>
          <input
            type="text"
            value={data.taxNumber || ''}
            onChange={(e) => updateField('taxNumber', e.target.value)}
            placeholder="Tax identification number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Business Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Briefcase className="w-4 h-4 inline mr-2" />
          Business Address
        </label>
        <textarea
          value={data.businessAddress || ''}
          onChange={(e) => updateField('businessAddress', e.target.value)}
          placeholder="Full registered business address"
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Business Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FileText className="w-4 h-4 inline mr-2" />
          Business Description *
        </label>
        <textarea
          value={data.bio || ''}
          onChange={(e) => updateField('bio', e.target.value)}
          onBlur={() => handleBlur('bio')}
          placeholder="Describe your company, services, and what makes you unique..."
          rows={4}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.bio && touched.bio ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.bio && touched.bio && (
          <p className="text-xs text-red-600 mt-1">{errors.bio}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">{data.bio?.length || 0} characters</p>
      </div>

      {/* Team & Capacity */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary-600" />
          Team & Capacity
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Employees
            </label>
            <input
              type="number"
              min="0"
              value={data.employeeCount || ''}
              onChange={(e) => updateField('employeeCount', parseInt(e.target.value) || '')}
              placeholder="Number of employees"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Qualified Technicians
            </label>
            <input
              type="number"
              min="0"
              value={data.technicianCount || ''}
              onChange={(e) => updateField('technicianCount', parseInt(e.target.value) || '')}
              placeholder="Number of qualified technicians"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fleet Size
            </label>
            <input
              type="number"
              min="0"
              value={data.fleetSize || ''}
              onChange={(e) => updateField('fleetSize', parseInt(e.target.value) || '')}
              placeholder="Number of vehicles"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Locations
            </label>
            <input
              type="number"
              min="0"
              value={data.serviceLocations || ''}
              onChange={(e) => updateField('serviceLocations', parseInt(e.target.value) || '')}
              placeholder="Number of offices/locations"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Operating Hours */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Hours & Availability</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={data.emergencyService || false}
                onChange={(e) => updateField('emergencyService', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">24/7 Emergency Service</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={data.weekendService || false}
                onChange={(e) => updateField('weekendService', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Weekend Service</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Average Response Time
            </label>
            <input
              type="text"
              value={data.responseTime || ''}
              onChange={(e) => updateField('responseTime', e.target.value)}
              placeholder="e.g., Within 2 hours"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
